import { useQuery } from "@apollo/client";
import moment from "moment-timezone";
import PropTypes from "prop-types";
import { useContext } from "react";
import { GlobalConfigContext } from "../../../context/GlobalConfigContext";
import { getCodeGroups } from "../../../graphql/queries/codeGroupQueries";
import { findControlInUISchema } from "../../../utils/commons";
import { getAllCodeGroupIds } from "../../jsonForms/shared/codeGroupUtils";
import { customComponents } from "./allComponents";

const ViewComponentsRenderer = ({
  customComponentName = "",
  data,
  inputSchema,
  uiSchema,
  definition,
  ruleDefinition,
  index,
  updateOutcome,
}) => {
  const receiptTitle = {
    end_timestamp: "Receipt End Date",
    start_timestamp: "Receipt Start Date",
  };
  const {
    globalState: { programConfig },
  } = useContext(GlobalConfigContext);
  const currentProgramTimezone = programConfig?.localization?.programTimezone;

  const { data: apiList } = useQuery(getCodeGroups, {
    variables: {
      filter: {
        limit: 1000,
      },
    },
    fetchPolicy: "cache-first",
    skip: !data?.code_group_ids,
  });

  const renderCodeGroupsDisplay = (codeGroupIds) => {
    const selectedGroups = [];
    const ids = getAllCodeGroupIds(codeGroupIds);
    if (ids?.length && apiList?.listCodeGroups?.length) {
      ids.forEach((id) => {
        const formattedGroup = apiList?.listCodeGroups?.find(
          (codeGroup) => codeGroup.codeGroupId === id
        );
        if (formattedGroup) {
          selectedGroups.push(formattedGroup);
        }
      });
    }
    return (
      <div className="additional-fields-container">
        <div className="field-pair" style={{ flexBasis: "31% !important" }}>
          <span className="field-label">Code Groups: </span>
        </div>
        <div className="field-pair-two-column">
          <span className="field-value">
            {selectedGroups.length
              ? selectedGroups.map((codeGroup) => (
                  <span
                    key={codeGroup.codeGroupId}
                    style={{
                      backgroundColor:
                        codeGroup.compositeStatus === "INACTIVE"
                          ? "#dfdfdf"
                          : "inherit",
                      marginRight: "8px",
                    }}
                  >
                    {codeGroup?.basicSetup?.codeGroupName || ""}
                  </span>
                ))
              : "-"}
          </span>
        </div>
      </div>
    );
  };

  const formatData = (schema, uischema, data) => {
    if (schema?.type === "boolean") {
      return data ? "Yes" : "No";
    }
    if (
      schema?.type === "array" &&
      schema.items?.type === "string" &&
      Array.isArray(data)
    ) {
      return data.join(", ");
    }
    if (schema?.date_format || schema.is_timestamp) {
      return moment.tz(data, currentProgramTimezone).format("Do MMMM YYYY hh:mm A");
    }
    if (uischema?.options?.labelMapping) {
      return uischema.options.labelMapping[data] || data;
    }
    if (data?.code_group_ids) {
      return renderCodeGroupsDisplay(data.code_group_ids);
    }
    return data === undefined || data === null || data === "" ? "-" : data;
  };

  const renderData = (schema, uischema, data) => {
    if (
      !schema ||
      Object.keys(schema).length === 0 ||
      data === undefined ||
      data === null
    ) {
      return ruleDefinition?.name ? (
        <div className="additional-fields-container">
          <div className="field-pair">
            <span className="field-label">{ruleDefinition?.name || "-"}</span>
          </div>
        </div>
      ) : (
        <></>
      );
    }
    if (data?.code_group_ids) {
      return renderCodeGroupsDisplay(data.code_group_ids);
    }
    if (schema.type === "object") {
      return (
        <>
          {Object.entries(schema.properties).map(([key, propertySchema]) => {
            const title =
              customComponentName?.length > 0 &&
              customComponentName === "receipt-in-date-range"
                ? receiptTitle[key]
                : propertySchema.title || key;
            const value = data[key];
            const controlSchema = findControlInUISchema(
              uischema,
              `#/properties/${key}`
            );
            if (propertySchema.hidden === true) {
              return <></>;
            }
            return (
              <div key={key} className="additional-fields-container">
                <div className="field-pair">
                  <span className="field-label">{title}:</span>
                </div>
                <div className="field-pair-two-column">
                  <span className="field-value">
                    {renderData(propertySchema, controlSchema, value)}
                  </span>
                </div>
              </div>
            );
          })}
        </>
      );
    } else if (
      schema.type === "array" &&
      Array.isArray(data) &&
      schema.items?.type !== "string"
    ) {
      return (
        <>
          {data.map((item) =>
            schema.items ? (
              renderData(schema.items, uischema, item)
            ) : (
              <p key={item} className="field-value">
                {item}
              </p>
            )
          )}
        </>
      );
    } else {
      return <>{formatData(schema, uischema, data)}</>;
    }
  };

  const CustomComponent = customComponents[customComponentName];

  return (
    <>
      {CustomComponent ? (
        <CustomComponent
          data={data}
          inputSchema={inputSchema}
          customComponentName={customComponentName}
          definition={definition}
          index={index}
          updateOutcome={updateOutcome}
        />
      ) : (
        renderData(inputSchema, uiSchema, data)
      )}
    </>
  );
};
ViewComponentsRenderer.propTypes = {
  customComponentName: PropTypes.string,
  data: PropTypes.any,
  inputSchema: PropTypes.object,
  uiSchema: PropTypes.object,
};

export default ViewComponentsRenderer;
